import {
  BffTrimData,
  ChargeDelta,
  ChargerTypeValues,
  ChargeTimeTenantConfiguration,
  ChargeTimeTrimModel,
  FinalSoc,
  OutsideTemperatures,
  StartSoc,
  ChargingLocation,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {
  ChargingPowerValues,
  CommonFeatureServices,
  formatModelSelectId,
  formatTrimSelectId,
  FormField,
  getTrimAndVariantForModelId,
  TrackingEvent,
  useConfigurationsContext,
  useFeatureAppId,
  useFeatureService,
  useFormField,
  useTrackingFunctions,
  YesNoValues,
  getSelectedTrim,
  getSelectedModel,
  ChargetimeFeatureAppContentConfig,
  useGetAppDataContent,
  getDefaultOutsideTemperature,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {useTranslation} from './use-translation';
import React, {useCallback} from 'react';
import {useTrims} from './use-trims';
import {useTenantConfiguration} from './use-tenant-configuration';
import {Logger} from '@feature-hub/core';

export interface ChargeTimeCalculationFormState {
  /** Outside temperature in °C */
  outsideTemperature: OutsideTemperatures;

  /** Where vehicle will be charged */
  chargingLocation: ChargingLocation;

  /**  Which model type / "trim "is selected */
  modelTypeId: string;

  /**  Which exact model / engine is selected */
  modelEngineId: string;

  /** [starting charge, intended charge] in % */
  chargeDelta: [StartSoc, FinalSoc];

  /** Which charging location type [householdSocket, wallboxAC, chargingStationDC] */
  chargerType: ChargerTypeValues;

  /** What charging power to use [2.3 kW, 11 kW, etc.] */
  chargingPower: ChargingPowerValues;

  /** Use pre-heated or non-pre-heated battery simulation data */
  preHeatedBattery: YesNoValues;
}

const getDefaultValuesForTrim = (
  trim: BffTrimData<ChargeTimeTrimModel>
): Pick<ChargeTimeCalculationFormState, 'modelEngineId' | 'modelTypeId'> => {
  const modelTypeId = formatTrimSelectId(trim);
  const firstModel = trim.modelVariants[0];

  return {
    modelTypeId,
    modelEngineId: formatModelSelectId(firstModel),
  };
};

const getDefaultState = (
  trim: BffTrimData<ChargeTimeTrimModel>,
  tenantConfig: ChargeTimeTenantConfiguration,
  chargeDelta: ChargeDelta | undefined,
  appDataContent?: ChargetimeFeatureAppContentConfig,
  logger?: Logger
): ChargeTimeCalculationFormState => ({
  outsideTemperature: getDefaultOutsideTemperature(
    appDataContent?.temperature,
    tenantConfig?.inputSection?.temperatureQuestion?.range?.default,
    logger
  ),
  chargingLocation:
    tenantConfig.resultSection.chargeOptions.default ?? ChargingLocation.home,
  chargeDelta: chargeDelta ?? [10, 80], // %,
  chargerType: ChargerTypeValues.socketAC,
  chargingPower: ChargingPowerValues.kW23,
  preHeatedBattery: tenantConfig.inputSection.preHeatedBatteryQuestion
    ?.usePreHeatedBatteryByDefault
    ? YesNoValues.yes
    : YesNoValues.no,
  ...getDefaultValuesForTrim(trim),
});

export type ChargeTimeCalculationFormFields = {
  [Field in keyof ChargeTimeCalculationFormState]: FormField<
    ChargeTimeCalculationFormState[Field]
  >;
};

export interface ChargeTimeCalculationForm {
  formFields: ChargeTimeCalculationFormFields;
  formState: ChargeTimeCalculationFormState;
  selectedTrim: BffTrimData<ChargeTimeTrimModel>;
  selectedModel: ChargeTimeTrimModel;
}

export function useChargeTimeCalculationForm(): ChargeTimeCalculationForm {
  const baseId = useFeatureAppId();
  const trims = useTrims();

  const tenantConfiguration = useTenantConfiguration();
  const translations = useTranslation();
  const logger = useFeatureService(CommonFeatureServices.s2Logger);
  const layerButtonCustomChannel = useFeatureService(
    CommonFeatureServices.layerButtonChanelService
  );
  const tracking = useTrackingFunctions();

  const appDataContent =
    useGetAppDataContent<ChargetimeFeatureAppContentConfig>(
      useConfigurationsContext().appDataContent
    );
  const trimForModelId =
    appDataContent &&
    appDataContent.defaultModelId &&
    appDataContent.defaultModelId.length > 0 &&
    getTrimAndVariantForModelId(trims, appDataContent.defaultModelId);

  const startSocDefault = tenantConfiguration?.inputSection?.socQuestion
    ?.default?.startSoc as StartSoc | undefined;
  const finalSocDefault = tenantConfiguration?.inputSection?.socQuestion
    ?.default?.finalSoc as FinalSoc | undefined;

  const chargeDeltaDefault: ChargeDelta | undefined =
    typeof startSocDefault === 'number' && typeof finalSocDefault === 'number'
      ? [startSocDefault, finalSocDefault]
      : undefined;

  const defaultState = getDefaultState(
    trimForModelId || trims[0],
    tenantConfiguration,
    chargeDeltaDefault,
    appDataContent,
    logger
  );

  // use refs here to be able to reference these within onChange side effects
  const formFieldsRef =
    React.useRef<ChargeTimeCalculationFormFields | null>(null);
  const selectedTrimRef = React.useRef<BffTrimData<ChargeTimeTrimModel>>(
    trims[0]
  );
  const selectedModelRef = React.useRef<ChargeTimeTrimModel>(
    trims[0].modelVariants[0]
  );

  const outsideTemperature = useFormField(
    `${baseId}-outside-temperature-id`,
    defaultState.outsideTemperature,
    translations.inputSection.temperatureQuestion.heading,
    React.useCallback(
      (newValue: number) => {
        tracking[TrackingEvent.OutsideTemperatureChange]?.({
          temperatureCelsius: newValue,
        });
      },
      [tracking]
    )
  );

  const chargingLocation = useFormField(
    `${baseId}-charging-location-id`,
    defaultState.chargingLocation,
    translations.inputSection.loadingLocationQuestion.heading,
    React.useCallback(
      (newValue: ChargingLocation) =>
        tracking[TrackingEvent.ChargingLocationChange]?.({
          location: newValue,
        }),
      [tracking]
    )
  );

  const chargerType = useFormField(
    `${baseId}-charger-type-id`,
    defaultState.chargerType,
    translations.inputSection.loadingLocationQuestion.heading,
    React.useCallback(
      (newValue: ChargerTypeValues) =>
        tracking[TrackingEvent.ChargerTypeChange]?.({
          chargerType: newValue,
        }),
      [tracking]
    )
  );

  const chargingPower = useFormField(
    `${baseId}-charging-power-id`,
    defaultState.chargingPower,
    translations.inputSection.loadingLocationQuestion.heading,
    React.useCallback(
      (newValue: ChargingPowerValues) =>
        tracking[TrackingEvent.ChargingPowerChange]?.({
          chargingPower: newValue,
        }),
      [tracking]
    )
  );

  const modelTypeId = useFormField(
    `${baseId}-trim-id`,
    defaultState.modelTypeId,
    translations.carSelectionSection.modelAndMotorSelection.modelLabel,
    React.useCallback(
      (newValue: string, shared?: boolean) => {
        const nextSelectedTrim = getSelectedTrim<ChargeTimeTrimModel>(
          trims,
          newValue
        );
        const next = getDefaultValuesForTrim(nextSelectedTrim);

        const nextSelectedModel = getSelectedModel<ChargeTimeTrimModel>(
          nextSelectedTrim,
          next.modelEngineId
        );
        selectedTrimRef.current = nextSelectedTrim;
        formFieldsRef.current?.modelEngineId.onChangeWithoutSideEffect(
          next.modelEngineId
        );

        if (layerButtonCustomChannel) {
          layerButtonCustomChannel.client.set({
            modelTypeId: next.modelTypeId,
          });
        }

        if (!shared) {
          tracking[TrackingEvent.ChargeTimeTrimSelect]?.({
            trim: nextSelectedTrim,
            model: nextSelectedModel,
          });
        }
      },
      [formFieldsRef, tracking, trims]
    )
  );

  const modelEngineId = useFormField(
    `${baseId}-trim-id`,
    defaultState.modelEngineId,
    translations.carSelectionSection.modelAndMotorSelection.motorLabel,
    useCallback(
      (newValue: string) => {
        const nextSelectedModel = getSelectedModel<ChargeTimeTrimModel>(
          selectedTrimRef.current,
          newValue
        );

        tracking[TrackingEvent.ChargeTimeEngineSelect]?.({
          model: nextSelectedModel,
        });
      },
      [selectedTrimRef, tracking]
    )
  );

  const chargeDelta = useFormField(
    `${baseId}-charge-delta-id`,
    defaultState.chargeDelta,
    translations.inputSection.socQuestion.heading,
    useCallback(
      (values: [StartSoc, FinalSoc]) =>
        tracking[TrackingEvent.ChargeDeltaChange]?.({
          percentages: values,
        }),
      [tracking]
    )
  );

  /** Has preHeated toggle been set by the user already? */
  const preHeatedBatteryTouched = React.useRef(false);

  const preHeatedBattery = useFormField(
    `${baseId}-pre-heated-battery`,
    defaultState.preHeatedBattery,
    translations.inputSection.preHeatedBatteryQuestion?.heading ?? '',
    React.useCallback(
      (value: YesNoValues) => {
        preHeatedBatteryTouched.current = true;
        tracking[TrackingEvent.PreHeatedBattery]?.({
          value,
          name: 'preheated-toggle-button',
        });
      },
      [preHeatedBatteryTouched, tracking]
    )
  );

  const selectedTrim = React.useMemo(
    () => getSelectedTrim<ChargeTimeTrimModel>(trims, modelTypeId.value),
    [trims, modelTypeId.value]
  );

  const selectedModel = React.useMemo(
    () =>
      getSelectedModel<ChargeTimeTrimModel>(selectedTrim, modelEngineId.value),
    [selectedTrim, modelEngineId.value]
  );

  const formFields: ChargeTimeCalculationFormFields = React.useMemo(
    () => ({
      outsideTemperature,
      chargingLocation,
      modelTypeId,
      modelEngineId,
      chargeDelta,
      chargerType,
      chargingPower,
      preHeatedBattery,
    }),
    [
      outsideTemperature,
      chargingLocation,
      modelTypeId,
      modelEngineId,
      chargeDelta,
      chargerType,
      chargingPower,
      preHeatedBattery,
    ]
  );

  const formState: ChargeTimeCalculationFormState = React.useMemo(
    () => ({
      outsideTemperature: outsideTemperature.value,
      chargingLocation: chargingLocation.value,
      modelTypeId: modelTypeId.value,
      modelEngineId: modelEngineId.value,
      chargeDelta: chargeDelta.value,
      chargerType: chargerType.value,
      chargingPower: chargingPower.value,
      preHeatedBattery: preHeatedBattery.value,
    }),
    [
      outsideTemperature.value,
      chargingLocation.value,
      modelTypeId.value,
      modelEngineId.value,
      chargeDelta.value,
      chargerType.value,
      chargingPower.value,
      preHeatedBattery.value,
    ]
  );

  // Important: update the ref values so that side effects reference current data
  formFieldsRef.current = formFields;
  selectedTrimRef.current = selectedTrim;
  selectedModelRef.current = selectedModel;

  return {
    formFields,
    formState,
    selectedTrim,
    selectedModel,
  };
}
